/**
 * Labour tab (Phase 4): automatic per-stage summary from the shared rollup,
 * editable, with a "Check and approve" grouped view and Approve button.
 */
import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { fmtNZD } from "@/lib/api";
import NumberInput from "@/components/NumberInput";

export default function LabourSummaryTab() {
  const { project } = useOutletContext();
  const [rollup, setRollup] = useState(null);
  const [stages, setStages] = useState([]);
  const [reviewMode, setReviewMode] = useState(false);
  const [approvedAt, setApprovedAt] = useState(null);
  const [msg, setMsg] = useState("");

  async function load() {
    const [sl, s] = await Promise.all([
      api.get(`/projects/${project.id}/stage-labour`),
      api.get(`/projects/${project.id}/stages`).catch(() => ({ data: [] })),
    ]);
    setRollup(sl.data); setStages(s.data || []);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [project.id]);

  async function saveStage(sid, hours) {
    await api.patch(`/stages/${sid}`, { labour_hours_budget: parseFloat(hours || 0) });
    await load();
  }
  async function resetStage(sid) {
    await api.post(`/stages/${sid}/reset-to-estimate`);
    await load();
  }

  if (!rollup) return <div className="opacity-60">Loading labour summary…</div>;
  const rows = rollup.stages || [];
  const totalBudget = rows.reduce((s, r) => s + (r.budget || 0), 0);
  const totalUsed = rows.reduce((s, r) => s + (r.used || 0), 0);

  return (
    <div className="space-y-3" data-testid="labour-summary-tab">
      <div className="card flex gap-2 items-end flex-wrap">
        <h3 className="text-silver flex-1">Per-stage labour summary <span className="opacity-60 text-xs">(auto from Materials + Labour, shared rollup)</span></h3>
        <button className={`btn-${reviewMode ? "ghost" : "silver"}`} onClick={()=>setReviewMode(x=>!x)} data-testid="btn-review-mode">
          {reviewMode ? "Back to edit" : "Check and approve"}
        </button>
      </div>

      {msg && <div className="text-green-400 text-sm">{msg}</div>}

      {!reviewMode && (
        <div className="card">
          <table className="table text-sm">
            <thead><tr>
              <th>Stage</th>
              <th className="text-right">Budget (h)</th>
              <th className="text-right">From estimate (h)</th>
              <th className="text-right">Used (h)</th>
              <th className="text-right">Remaining (h)</th>
              <th>Source</th>
            </tr></thead>
            <tbody>{rows.map(r => (
              <tr key={r.id} data-testid={`ls-row-${r.id}`}>
                <td>{r.name}</td>
                <td>
                  <NumberInput className="input w-24 text-right"
                    defaultValue={r.budget}
                    onBlur={e => saveStage(r.id, e.target.value)}
                    data-testid={`ls-budget-${r.id}`}/>
                </td>
                <td className="text-right">{r.from_estimate_hours.toFixed(2)}</td>
                <td className="text-right">{r.used.toFixed(2)}</td>
                <td className={`text-right ${r.remaining < 0 ? "text-red-400" : ""}`}>{r.remaining.toFixed(2)}</td>
                <td>
                  {r.source === "manual" && (
                    <>
                      <span className="text-[10px] border border-amber-400 text-amber-300 px-1 rounded" data-testid={`ls-src-manual-${r.id}`}>manual</span>
                      <button className="hub-link text-[11px] ml-2" onClick={()=>resetStage(r.id)} data-testid={`ls-reset-${r.id}`}>
                        Reset to estimate ({r.from_estimate_hours}h)
                      </button>
                    </>
                  )}
                  {r.source !== "manual" && <span className="text-[10px] bg-silver text-black px-1 rounded">{r.source}</span>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan="6" className="opacity-50">No stages yet.</td></tr>}
            </tbody>
          </table>
          <div className="mt-2 text-xs opacity-70">Total budget: <b className="text-silver">{totalBudget.toFixed(2)}h</b> · Used: <b>{totalUsed.toFixed(2)}h</b></div>
        </div>
      )}

      {reviewMode && (
        <div className="card" data-testid="check-and-approve">
          <h4 className="text-silver mb-2">Check and approve — grouped by stage</h4>
          <table className="table text-sm">
            <thead><tr><th>Stage</th><th className="text-right">Hours</th><th>Source</th></tr></thead>
            <tbody>{rows.map(r => (
              <tr key={r.id} data-testid={`ca-row-${r.id}`}>
                <td>{r.name}</td>
                <td className="text-right"><b>{r.budget.toFixed(2)} h</b></td>
                <td className="text-xs opacity-70">{r.source}{r.source !== "manual" ? ` · ${r.from_estimate_hours.toFixed(2)}h from estimate` : ""}</td>
              </tr>
            ))}
            <tr className="font-bold text-silver"><td>TOTAL</td><td className="text-right">{totalBudget.toFixed(2)} h</td><td></td></tr>
            </tbody>
          </table>
          <button className="btn-silver mt-3" data-testid="btn-approve-labour"
                  onClick={async () => {
                    await api.patch(`/projects/${project.id}`, { labour_approved_at: new Date().toISOString(), labour_approved_hours: totalBudget });
                    setApprovedAt(new Date().toISOString());
                    setMsg(`Approved ✓ — snapshot of ${totalBudget.toFixed(2)} h saved on this project.`);
                    setTimeout(()=>setMsg(""), 4000);
                  }}>
            Approve this breakdown
          </button>
          {approvedAt && <span className="text-xs opacity-70 ml-2" data-testid="ca-approved-at">Approved at {approvedAt.slice(0,16).replace("T"," ")}</span>}
        </div>
      )}
    </div>
  );
}
